/**
 * Widget Actions Rapides Propriétaire
 * Phase 3.2 - Actions contextuelles optimisées
 */

import React from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Chip
} from '@mui/material';
import {
  AddBusiness as AddBusinessIcon,
  Block as BlockIcon,
  CalendarMonth as CalendarIcon,
  Download as DownloadIcon,
  Assessment as AssessmentIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { DashboardWidget } from '../DashboardBase';
import { QUICK_ACTIONS, DASHBOARD_TYPES } from '../../../constants/dashboardConstants';

// Mapping des icônes
const ICON_COMPONENTS = {
  AddBusiness: AddBusinessIcon,
  Block: BlockIcon,
  CalendarMonth: CalendarIcon,
  Download: DownloadIcon,
  Assessment: AssessmentIcon,
  Settings: SettingsIcon
};

const OwnerQuickActionsWidget = ({ actions, loading }) => {
  const navigate = useNavigate();

  // Actions par défaut si non fournies par le backend
  const defaultActions = QUICK_ACTIONS[DASHBOARD_TYPES.OWNER];
  const actionsList = actions || defaultActions;

  const handleActionClick = (action) => {
    switch (action.action) {
      case 'add_listing':
        navigate('/owner/listings/new');
        break;
      case 'block_dates':
        navigate('/owner/calendar?action=block');
        break;
      case 'view_calendar':
        navigate('/owner/calendar');
        break;
      case 'export_data':
        // TODO: Implémenter l'export
        console.log('Export data');
        break;
      case 'manage_settings':
        navigate('/owner/settings');
        break;
      default:
        console.log('Action non gérée:', action.action);
    }
  };

  // Actions supplémentaires contextuelles
  const contextualActions = [
    {
      action: 'view_analytics',
      label: 'Voir les analyses',
      icon: 'Assessment',
      color: 'info',
      badge: 'Nouveau'
    },
    {
      action: 'manage_settings',
      label: 'Paramètres',
      icon: 'Settings',
      color: 'secondary'
    }
  ];

  const allActions = [...actionsList, ...contextualActions];

  return (
    <DashboardWidget
      title="Actions rapides"
      loading={loading}
      height={400}
    >
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Accès rapide aux fonctionnalités principales
      </Typography>

      <List>
        {allActions.map((action, index) => {
          const IconComponent = ICON_COMPONENTS[action.icon] || AddBusinessIcon;
          
          return (
            <ListItem key={index} disablePadding>
              <ListItemButton
                onClick={() => handleActionClick(action)}
                sx={{
                  borderRadius: 2,
                  mb: 1,
                  '&:hover': {
                    bgcolor: `${action.color || 'primary'}.50`
                  }
                }}
              >
                <ListItemIcon>
                  <Avatar
                    sx={{
                      bgcolor: `${action.color || 'primary'}.main`,
                      width: 40,
                      height: 40
                    }}
                  >
                    <IconComponent />
                  </Avatar>
                </ListItemIcon>
                
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Typography variant="body1" fontWeight="medium">
                        {action.label}
                      </Typography>
                      {action.badge && (
                        <Chip
                          label={action.badge}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  }
                  secondary={action.description}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      {/* Conseils contextuels */}
      <Box mt={2} p={2} bgcolor="grey.50" borderRadius={2}>
        <Typography variant="caption" color="text.secondary">
          💡 <strong>Conseil :</strong> Utilisez le calendrier unifié pour une vue d'ensemble 
          de toutes vos disponibilités et réservations.
        </Typography>
      </Box>
    </DashboardWidget>
  );
};

export default OwnerQuickActionsWidget;
